import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, doc, deleteDoc } from 'firebase/firestore';
import { firestore } from '../lib/firebaseConfig';
import BlockTitle from './BlockTitle';
import Button from './Button';
import FormInput from './FormInput';
import FormLabel from './FormLabel';
import Modal from './Modal';
import ConfirmDialog from './ConfirmDialog';
import { useToast } from '../context/ToastContext';
import { resolveVatRate, RESIDENTIAL_VAT_RATE } from '../lib/vat';

const INVOICE_TYPES = {
  parking: { label: 'Miejsce parkingowe', description: 'Oplata za uzytkowanie miejsca parkingowego' },
  surface_correction: { label: 'Korekta powierzchni', description: 'Korekta kosztu z tytulu roznicy powierzchni' },
};

const formatPLN = (amount) =>
  new Intl.NumberFormat('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount) + ' zl';

const formatPLNInvoice = (amount) => {
  const formatted = amount.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${formatted} zl`;
};

const formatDatePolish = (dateStr) => {
  const months = {
    '01': 'stycznia', '02': 'lutego', '03': 'marca', '04': 'kwietnia',
    '05': 'maja', '06': 'czerwca', '07': 'lipca', '08': 'sierpnia',
    '09': 'wrzesnia', '10': 'pazdziernika', '11': 'listopada', '12': 'grudnia',
  };
  if (!dateStr) return dateStr;
  const [y, m, d] = dateStr.split('-');
  return `${parseInt(d)} ${months[m] || m} ${y}`;
};

const FinalInvoicesSection = ({ investmentId, flatId, flatDetails, clientsDetails, investmentDetails }) => {
  const { showSuccess, showError } = useToast();
  const [finalInvoices, setFinalInvoices] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('parking');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [loading, setLoading] = useState(true);

  const today = new Date().toISOString().split('T')[0];
  const [formData, setFormData] = useState({
    amountNet: 0,
    vatRate: RESIDENTIAL_VAT_RATE,
    saleDate: today,
    description: '',
    invoiceNumber: '',
  });

  // Fetch existing final invoices
  useEffect(() => {
    const fetchInvoices = async () => {
      if (!investmentId || !flatId) return;
      try {
        const snap = await getDocs(
          collection(firestore, `investments/${investmentId}/flats/${flatId}/final_invoices`)
        );
        setFinalInvoices(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error('Error fetching final invoices:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchInvoices();
  }, [investmentId, flatId]);

  const openModal = (type) => {
    const flatCode = flatDetails?.code || 'XX';
    const defaultVat = resolveVatRate(flatDetails);
    const suffix = type === 'parking' ? 'P' : 'K';

    setModalType(type);
    setFormData({
      amountNet: 0,
      vatRate: defaultVat,
      saleDate: today,
      description: INVOICE_TYPES[type].description,
      invoiceNumber: `FV/${new Date().getFullYear()}/${flatCode}/${suffix}`,
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    const net = parseFloat(formData.amountNet) || 0;
    const vatRate = formData.vatRate === null ? 0 : parseFloat(formData.vatRate);
    const vat = Math.round(net * vatRate * 100) / 100;
    const gross = Math.round((net + vat) * 100) / 100;

    const client = clientsDetails?.[0];
    const buyer = client?.buyers?.[0];

    const invoiceData = {
      type: modalType,
      typeLabel: INVOICE_TYPES[modalType].label,
      invoiceNumber: formData.invoiceNumber,
      description: formData.description,
      amountNet: net,
      amountVat: vat,
      amountGross: gross,
      vatRate: vatRate,
      saleDate: formData.saleDate,
      buyerName: buyer?.name || `${client?.name || ''} ${client?.surname || ''}`.trim(),
      buyerAddress: buyer?.address || '',
      buyerPesel: buyer?.pesel || '',
      createdAt: new Date().toISOString(),
    };

    try {
      const docRef = await addDoc(
        collection(firestore, `investments/${investmentId}/flats/${flatId}/final_invoices`),
        invoiceData
      );
      setFinalInvoices([...finalInvoices, { id: docRef.id, ...invoiceData }]);
      showSuccess(`Faktura ${INVOICE_TYPES[modalType].label} dodana`);
      setIsModalOpen(false);
    } catch (e) {
      console.error('Error saving final invoice:', e);
      showError('Blad zapisu faktury');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteDoc(doc(firestore, `investments/${investmentId}/flats/${flatId}/final_invoices/${deleteTarget.id}`));
      setFinalInvoices(finalInvoices.filter(i => i.id !== deleteTarget.id));
      showSuccess('Faktura usunieta');
    } catch (e) {
      showError('Blad usuwania');
    }
    setDeleteTarget(null);
  };

  const generatePDF = (invoice) => {
    const net = invoice.amountNet;
    const vat = invoice.amountVat;
    const gross = invoice.amountGross;
    const vatRatePercent = Math.round((invoice.vatRate || RESIDENTIAL_VAT_RATE) * 100);

    const sellerName = investmentDetails?.company?.name || 'DRUGA - SOVO GROUP Sp. z o.o. Sp. k.';
    const sellerAddress = investmentDetails?.company?.address || 'ul. Piekary 7, 61-823 Poznan';
    const sellerNip = investmentDetails?.company?.nip || '7781446354';
    const bankAccount = flatDetails?.account_number || '';

    const buyerNipHtml = invoice.buyerPesel
      ? `<p class="text-gray-600 mt-2">PESEL: ${invoice.buyerPesel}</p>`
      : '';

    const html = `<!DOCTYPE html>
<html lang="pl">
<head>
  <meta charset="UTF-8">
  <title>Faktura ${invoice.invoiceNumber}</title>
  <script src="https://cdn.tailwindcss.com"><\/script>
  <script>
    tailwind.config = { theme: { extend: { colors: { sovo: { primary: '#722F37' } } } } }
  <\/script>
  <style>@media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }</style>
</head>
<body class="bg-white">
  <div class="bg-white p-8 max-w-[210mm] mx-auto">
    <div class="flex justify-between items-start mb-8">
      <div>
        <h1 class="text-3xl font-bold text-gray-900">FAKTURA VAT</h1>
        <p class="text-xl text-gray-600 mt-1">${invoice.invoiceNumber}</p>
      </div>
    </div>
    <div class="grid grid-cols-3 gap-4 mb-8 text-sm">
      <div class="bg-gray-50 p-3 rounded"><p class="text-gray-500">Data wystawienia</p><p class="font-medium">${formatDatePolish(invoice.createdAt?.split('T')[0] || today)}</p></div>
      <div class="bg-gray-50 p-3 rounded"><p class="text-gray-500">Data sprzedazy</p><p class="font-medium">${formatDatePolish(invoice.saleDate)}</p></div>
      <div class="bg-gray-50 p-3 rounded"><p class="text-gray-500">Sposob zaplaty</p><p class="font-medium">Przelew bankowy</p></div>
    </div>
    <div class="grid grid-cols-2 gap-8 mb-8">
      <div class="flex flex-col">
        <h2 class="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Sprzedawca</h2>
        <div class="bg-sovo-primary/10 p-4 rounded-lg border-l-4 border-sovo-primary flex-1">
          <p class="font-bold text-gray-900">${sellerName}</p>
          <p class="text-gray-600">${sellerAddress}</p>
          <p class="text-gray-600 mt-2">NIP: ${sellerNip}</p>
        </div>
      </div>
      <div class="flex flex-col">
        <h2 class="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Nabywca</h2>
        <div class="bg-gray-50 p-4 rounded-lg border-l-4 border-gray-400 flex-1">
          <p class="font-bold text-gray-900">${invoice.buyerName}</p>
          <p class="text-gray-600">${invoice.buyerAddress}</p>
          ${buyerNipHtml}
        </div>
      </div>
    </div>
    <div class="mb-8">
      <table class="w-full text-xs">
        <thead>
          <tr class="bg-sovo-primary text-white">
            <th class="py-2 px-2 text-left rounded-tl-lg w-8">Lp.</th>
            <th class="py-2 px-2 text-left">Nazwa</th>
            <th class="py-2 px-2 text-center w-12">Ilosc</th>
            <th class="py-2 px-2 text-center w-12">J.m.</th>
            <th class="py-2 px-2 text-right w-24">Cena netto</th>
            <th class="py-2 px-2 text-center w-12">VAT</th>
            <th class="py-2 px-2 text-right w-28">Wartosc netto</th>
            <th class="py-2 px-2 text-right rounded-tr-lg w-28">Wartosc brutto</th>
          </tr>
        </thead>
        <tbody>
          <tr class="bg-gray-50">
            <td class="py-2 px-2">1</td>
            <td class="py-2 px-2 font-medium">${invoice.description}</td>
            <td class="py-2 px-2 text-center">1</td>
            <td class="py-2 px-2 text-center">szt.</td>
            <td class="py-2 px-2 text-right">${formatPLNInvoice(net)}</td>
            <td class="py-2 px-2 text-center">${vatRatePercent}%</td>
            <td class="py-2 px-2 text-right">${formatPLNInvoice(net)}</td>
            <td class="py-2 px-2 text-right">${formatPLNInvoice(gross)}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <div class="flex justify-end mb-8">
      <div class="w-80">
        <div class="flex justify-between py-2 border-b text-sm"><span class="text-gray-600">Suma netto:</span><span class="font-medium">${formatPLNInvoice(net)}</span></div>
        <div class="flex justify-between py-2 border-b text-sm"><span class="text-gray-600">Suma VAT (${vatRatePercent}%):</span><span class="font-medium">${formatPLNInvoice(vat)}</span></div>
        <div class="flex justify-between py-3 bg-sovo-primary text-white px-4 rounded-lg mt-2"><span class="font-semibold">DO ZAPLATY:</span><span class="font-bold text-lg">${formatPLNInvoice(gross)}</span></div>
      </div>
    </div>
    <div class="bg-gray-50 p-4 rounded-lg mb-6">
      <h3 class="font-semibold text-gray-900 mb-2">Dane do przelewu</h3>
      <div class="grid grid-cols-2 gap-4 text-sm">
        <div><p class="text-gray-500">Bank</p><p class="font-medium">mBank S.A.</p></div>
        <div><p class="text-gray-500">Numer konta</p><p class="font-medium font-mono">${bankAccount}</p></div>
      </div>
    </div>
  </div>
</body>
</html>`;

    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
  };

  const hasParking = finalInvoices.some(i => i.type === 'parking');
  const hasCorrection = finalInvoices.some(i => i.type === 'surface_correction');

  if (loading) return null;

  return (
    <div className="mt-[28px] bg-white rounded-[6px] border border-[var(--bright-gray)] p-5">
      <div className="flex justify-between items-center">
        <BlockTitle>Faktury koncowe</BlockTitle>
        <div className="flex gap-2">
          {!hasParking && (
            <Button variant="bordered" className="text-xs" onClick={() => openModal('parking')}>
              + Miejsce parkingowe
            </Button>
          )}
          {!hasCorrection && (
            <Button variant="bordered" className="text-xs" onClick={() => openModal('surface_correction')}>
              + Korekta powierzchni
            </Button>
          )}
        </div>
      </div>

      {finalInvoices.length === 0 ? (
        <p className="text-gray-400 text-sm mt-3">Brak faktur koncowych</p>
      ) : (
        <table className="w-full mt-3 text-sm">
          <thead>
            <tr className="bg-[var(--lotion)] border-b border-[var(--bright-gray)]">
              <th className="text-left py-2 px-3">Typ</th>
              <th className="text-left py-2 px-3">Nr faktury</th>
              <th className="text-left py-2 px-3">Opis</th>
              <th className="text-right py-2 px-3">Netto</th>
              <th className="text-right py-2 px-3">Brutto</th>
              <th className="text-left py-2 px-3">Data sprzedazy</th>
              <th className="py-2 px-3"></th>
            </tr>
          </thead>
          <tbody>
            {finalInvoices.map((inv) => (
              <tr key={inv.id} className="border-b border-[var(--bright-gray)]">
                <td className="py-2 px-3">{inv.typeLabel}</td>
                <td className="py-2 px-3">{inv.invoiceNumber}</td>
                <td className="py-2 px-3">{inv.description}</td>
                <td className="py-2 px-3 text-right">{formatPLN(inv.amountNet)}</td>
                <td className="py-2 px-3 text-right">{formatPLN(inv.amountGross)}</td>
                <td className="py-2 px-3">{inv.saleDate}</td>
                <td className="py-2 px-3 flex gap-1">
                  <button onClick={() => generatePDF(inv)} className="text-[var(--maroon)] hover:opacity-70 text-xs underline">PDF</button>
                  <button onClick={() => setDeleteTarget(inv)} className="text-red-400 hover:text-red-600 text-xs">x</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Add invoice modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <div className="p-6">
          <BlockTitle>{INVOICE_TYPES[modalType]?.label}</BlockTitle>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <FormLabel>Numer faktury</FormLabel>
              <FormInput value={formData.invoiceNumber} onChange={(e) => setFormData({...formData, invoiceNumber: e.target.value})} />
            </div>
            <div>
              <FormLabel>Data sprzedazy</FormLabel>
              <FormInput type="date" value={formData.saleDate} onChange={(e) => setFormData({...formData, saleDate: e.target.value})} />
            </div>
            <div>
              <FormLabel>Kwota netto (PLN)</FormLabel>
              <FormInput type="number" step="0.01" value={formData.amountNet} onChange={(e) => setFormData({...formData, amountNet: e.target.value})} />
            </div>
            <div>
              <FormLabel>Stawka VAT</FormLabel>
              <select
                className="w-full h-[38px] border border-[#D7D7D7] rounded-[6px] px-2 text-sm"
                value={formData.vatRate}
                onChange={(e) => setFormData({...formData, vatRate: parseFloat(e.target.value)})}
              >
                <option value="0.08">8%</option>
                <option value="0.23">23%</option>
              </select>
            </div>
            <div className="col-span-2">
              <FormLabel>Opis</FormLabel>
              <FormInput value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} />
            </div>
          </div>
          {formData.amountNet > 0 && (
            <div className="mt-3 text-sm text-gray-500">
              Brutto: {formatPLN(parseFloat(formData.amountNet) * (1 + parseFloat(formData.vatRate)))}
            </div>
          )}
          <div className="mt-6 flex justify-end gap-3">
            <Button variant="bordered" onClick={() => setIsModalOpen(false)}>Anuluj</Button>
            <Button variant="default" onClick={handleSave}>Zapisz</Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={deleteTarget !== null}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        title="Usun fakture"
        message={`Usunac fakture ${deleteTarget?.typeLabel || ''}?`}
        confirmText="Usun"
        cancelText="Anuluj"
        variant="danger"
      />
    </div>
  );
};

export default FinalInvoicesSection;
